/**
 * PointsHistoryPage — Iter. 406.fr.62
 * ────────────────────────────────────
 * "Mes Points" — unified gamification history so the user finally
 * understands WHERE their points come from and HOW MUCH they've earned
 * across day / week / month / year.
 *
 * Displays the 3 co-existing subsystems distinctly:
 *   • Extra Miles — quality of every meal (2 → 20 pts)
 *   • Clash       — daily activity feeding the pack score
 *   • Glory       — badges unlocked (rarity-weighted, 10 → 400)
 *
 * Backed by GET /api/points/history?window=day|week|month|year
 */
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { useLanguage, api } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Sparkles, Trophy, Utensils, Zap, Info, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WINDOWS = ['day', 'week', 'month', 'year'];

export default function PointsHistoryPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';
  const [window, setWindow] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/points/history?window=${window}`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [window]);

  const windowLabel = useMemo(() => ({
    day: fr ? 'Aujourd\'hui' : 'Today',
    week: fr ? '7 derniers jours' : 'Last 7 days',
    month: fr ? '30 derniers jours' : 'Last 30 days',
    year: fr ? '365 derniers jours' : 'Last 365 days',
  }), [fr]);

  const totals = data?.totals || { extra_miles: 0, clash: 0, glory: 0, grand_total: 0 };
  const counts = data?.counts || { extra_miles: 0, clash: 0, glory: 0 };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="points-history-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-foreground">
            {fr ? 'Mes Points' : 'My Points'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {fr
              ? 'Comprends la gamification YUMIX en un coup d\'œil — 3 systèmes qui se combinent pour te faire progresser.'
              : 'Understand YUMIX gamification at a glance — 3 systems that combine to move you forward.'}
          </p>
        </div>

        {/* Window toggle */}
        <div className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-card/60 p-1" role="group" data-testid="points-window-toggle">
          {WINDOWS.map((w) => {
            const active = window === w;
            const label = { day: fr ? 'Jour' : 'Day', week: fr ? 'Semaine' : 'Week', month: fr ? 'Mois' : 'Month', year: fr ? 'Année' : 'Year' }[w];
            return (
              <button
                key={w}
                onClick={() => setWindow(w)}
                data-testid={`points-window-${w}`}
                className={`text-xs font-heading uppercase tracking-widest rounded-full px-3 py-1.5 transition-all ${
                  active
                    ? 'bg-primary text-primary-foreground shadow-[0_0_10px_rgba(251,146,60,0.4)]'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70">{windowLabel[window]}</p>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && data && (
          <>
            {/* Grand total banner */}
            <Card className="border border-primary/40 bg-gradient-to-r from-primary/10 via-amber-500/10 to-primary/10">
              <CardContent className="py-5 text-center">
                <p className="text-[11px] uppercase tracking-[6px] text-muted-foreground mb-1">{fr ? 'Total accumulé' : 'Total earned'}</p>
                <p className="font-heading text-5xl font-black text-primary" data-testid="points-grand-total">{totals.grand_total}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {fr ? `pts sur ${windowLabel[window].toLowerCase()}` : `pts over ${windowLabel[window].toLowerCase()}`}
                </p>
              </CardContent>
            </Card>

            {/* 3 subsystem cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SubsystemCard
                icon={<Utensils className="w-4 h-4" />}
                color="rose"
                label={fr ? 'Extra Miles' : 'Extra Miles'}
                sub={fr ? 'Qualité des repas' : 'Meal quality'}
                total={totals.extra_miles}
                count={counts.extra_miles}
                countUnit={fr ? 'repas' : 'meals'}
                explainer={fr ? '2 à 20 pts par repas selon les protéines, calories et health_score.' : '2 to 20 pts per meal based on protein, calories and health_score.'}
                testid="points-card-extra-miles"
              />
              <SubsystemCard
                icon={<Zap className="w-4 h-4" />}
                color="sky"
                label={fr ? 'Clash' : 'Clash'}
                sub={fr ? 'Compétition de meute' : 'Pack competition'}
                total={totals.clash}
                count={counts.clash}
                countUnit={fr ? 'jours' : 'days'}
                explainer={fr ? '10 pts / repas · 15 pts / hydratation ✓ · 20 pts / workout · 5 pts / pesée. Additionnés à ta meute.' : '10 pts / meal · 15 pts / hydration ✓ · 20 pts / workout · 5 pts / weigh-in. Feeds your pack.'}
                testid="points-card-clash"
              />
              <SubsystemCard
                icon={<Trophy className="w-4 h-4" />}
                color="amber"
                label={fr ? 'Glory' : 'Glory'}
                sub={fr ? 'Badges V2' : 'V2 Badges'}
                total={totals.glory}
                count={counts.glory}
                countUnit={fr ? 'badges' : 'badges'}
                explainer={fr ? '10 (Commun) → 25 (Peu commun) → 60 (Rare) → 150 (Épique) → 400 (Légendaire). Compte pour le Mur des Légendes.' : '10 (Common) → 25 (Uncommon) → 60 (Rare) → 150 (Epic) → 400 (Legendary). Feeds the Wall of Legends.'}
                testid="points-card-glory"
              />
            </div>

            {/* How gamification works */}
            <Card className="border border-border/50 bg-card/60">
              <CardContent className="py-4">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                  <Info className="w-3 h-3" />
                  {fr ? 'Comment fonctionne la gamification' : 'How the gamification works'}
                </p>
                <ul className="text-xs text-foreground/80 space-y-1.5 leading-relaxed">
                  <li>• <span className="text-rose-400 font-bold">Extra Miles</span> {fr ? 'récompense la QUALITÉ de chaque repas — plus ton health_score est haut, plus tu gagnes.' : 'rewards MEAL QUALITY — the higher your health_score, the more you earn.'}</li>
                  <li>• <span className="text-sky-400 font-bold">Clash</span> {fr ? 'récompense l\'ACTIVITÉ RÉGULIÈRE de la journée — chaque action compte pour ton équipe rouge/bleue.' : 'rewards DAILY CONSISTENCY — each action feeds your red/blue team.'}</li>
                  <li>• <span className="text-amber-400 font-bold">Glory</span> {fr ? 'récompense les EXPLOITS DURABLES — badges rares, streaks longs, milestones.' : 'rewards LASTING FEATS — rare badges, long streaks, milestones.'}</li>
                </ul>
              </CardContent>
            </Card>

            {/* Timeline */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">
                {fr ? 'Historique détaillé' : 'Detailed history'}
              </p>
              <div className="space-y-2" data-testid="points-timeline">
                {data.timeline.length === 0 && (
                  <p className="text-sm text-muted-foreground/60 text-center py-6">
                    {fr ? 'Aucun point sur cette période — logge un repas pour démarrer !' : 'No points on this period — log a meal to get started!'}
                  </p>
                )}
                {data.timeline.map((e, i) => (
                  <TimelineRow key={i} event={e} fr={fr} />
                ))}
              </div>
            </div>

            {/* CTA to Clash */}
            <Card className="border border-primary/30 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors" onClick={() => navigate('/clash')}>
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-foreground">{fr ? 'Voir le Clash des Meutes' : 'See the Pack Clash'}</p>
                  <p className="text-xs text-muted-foreground">{fr ? 'Compare ton score à celui de ta meute' : 'Compare your score with your pack'}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-primary" />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

const COLORS = {
  rose: { border: 'border-rose-500/40', bg: 'bg-rose-500/10', text: 'text-rose-300', icon: 'text-rose-400' },
  sky: { border: 'border-sky-500/40', bg: 'bg-sky-500/10', text: 'text-sky-300', icon: 'text-sky-400' },
  amber: { border: 'border-amber-500/40', bg: 'bg-amber-500/10', text: 'text-amber-300', icon: 'text-amber-400' },
};

const SubsystemCard = ({ icon, color, label, sub, total, count, countUnit, explainer, testid }) => {
  const c = COLORS[color];
  return (
    <Card className={`${c.border} border ${c.bg}`} data-testid={testid}>
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={c.icon}>{icon}</span>
          <p className={`text-[11px] uppercase tracking-widest font-heading font-bold ${c.text}`}>{label}</p>
        </div>
        <p className={`font-heading text-3xl font-black ${c.text}`}>{total}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{sub}</p>
        <p className="text-[10px] text-muted-foreground/70 mt-1">{count} {countUnit}</p>
        <p className="text-[10px] text-muted-foreground/60 leading-relaxed mt-2">{explainer}</p>
      </CardContent>
    </Card>
  );
};

const SUBSYSTEM_STYLE = {
  extra_miles: { color: 'text-rose-300', dot: 'bg-rose-400', label: 'Extra Miles' },
  clash: { color: 'text-sky-300', dot: 'bg-sky-400', label: 'Clash' },
  glory: { color: 'text-amber-300', dot: 'bg-amber-400', label: 'Glory' },
};

const QUALITY_STYLE = {
  elite:   { ring: 'ring-emerald-400/60 bg-emerald-500/15 text-emerald-200' },
  good:    { ring: 'ring-sky-400/60 bg-sky-500/15 text-sky-200' },
  average: { ring: 'ring-amber-400/60 bg-amber-500/15 text-amber-200' },
  poor:    { ring: 'ring-red-400/70 bg-red-500/15 text-red-200' },
};

const TimelineRow = ({ event, fr }) => {
  const s = SUBSYSTEM_STYLE[event.subsystem] || { color: 'text-foreground', dot: 'bg-muted-foreground', label: event.subsystem };
  const when = event.when ? new Date(event.when) : null;
  const whenLabel = when && !isNaN(when.getTime())
    ? when.toLocaleString(fr ? 'fr-CA' : 'en-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';
  // Iter. 411.m — Quality tier chip (💎/✅/⚠️/⛔) surfaced from backend so
  // clients immediately see WHY a meal-related row rewarded X points.
  const q = event.quality && event.quality.icon ? event.quality : null;
  const qStyle = q ? (QUALITY_STYLE[q.tier] || QUALITY_STYLE.average) : null;
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border/40 bg-card/60 px-3 py-2">
      <div className="flex items-center gap-3">
        <div className={`shrink-0 w-2 h-2 rounded-full ${s.dot} shadow-[0_0_8px_currentColor]`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
            {q && (
              <span
                className={`shrink-0 inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-[11px] rounded-md ring-1 ${qStyle.ring}`}
                title={fr ? `Qualité: ${q.label_fr}` : `Quality: ${q.label_fr}`}
                data-testid={`points-quality-${q.tier}`}
              >
                {q.icon}
              </span>
            )}
            <span className="truncate">
              {event.meta?.emoji ? `${event.meta.emoji} ` : ''}{event.label}
            </span>
          </p>
          <p className="text-[10px] text-muted-foreground truncate">
            <span className={s.color}>{s.label}</span>
            {event.reason ? ` · ${event.reason}` : ''}
            {whenLabel ? ` · ${whenLabel}` : ''}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          <Sparkles className={`w-3 h-3 ${s.color}`} />
          <span className={`font-heading text-lg font-black ${s.color}`}>+{event.points}</span>
        </div>
      </div>
      {/* Iter. 411.o — AI micro-nudge sous les repas moyens/junk food */}
      {event.nudge && (
        <p
          className="text-[10px] italic leading-snug pl-5 text-amber-200/80"
          data-testid="points-nudge"
        >
          💡 {event.nudge}
        </p>
      )}
    </div>
  );
};
